import styled, { css } from "styled-components";

import Button from "../../components/Buttons/Button";
import { pxToRem, withAlpha } from "../../styles/utils";

export const AssignmentPageShell = styled.div`
  width: 100%;
  max-width: ${pxToRem(800)};
  padding-bottom: ${pxToRem(28)};

  @media (max-width: 720px) {
    padding-bottom: ${({ theme }) => pxToRem(theme.spacing.sm)};
  }
`;

export const AssignmentTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
  flex-wrap: wrap;
  margin-top: ${({ theme }) => pxToRem(theme.fontSize.sm)};
`;

export const AssignmentTitle = styled.h1`
  margin: 0;
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xl)};
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.02em;
`;

export const AssignmentMeta = styled.div`
  margin-top: ${pxToRem(6)};
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.45;
`;

export const AssignmentSectionHeading = styled.h2`
  margin: ${pxToRem(28)} 0 ${({ theme }) => pxToRem(theme.spacing.sm)};
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  font-weight: 600;
  line-height: 1.4;
`;

export const AssignmentSection = styled.section`
  padding: ${pxToRem(18)};
  border: ${pxToRem(1)} solid ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  background: ${({ theme }) => theme.colors.white};
`;

export const AssignmentRichText = styled.div`
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  line-height: 1.65;
  white-space: normal;

  p {
    margin: 0 0 ${({ theme }) => pxToRem(theme.spacing.sm)};
  }

  p:last-child {
    margin-bottom: 0;
  }

  ul,
  ol {
    margin: 0 0 ${({ theme }) => pxToRem(theme.spacing.sm)};
    padding-left: ${({ theme }) => pxToRem(theme.spacing.lg)};
  }

  li {
    margin-bottom: ${pxToRem(5)};
  }
`;

export const AssignmentPlainText = styled.div`
  color: ${({ theme }) => theme.colors.ink};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.md)};
  line-height: 1.65;
  white-space: pre-wrap;
`;

export const AssignmentReview = styled.section<{ $rated: boolean }>`
  padding: ${({ theme }) => pxToRem(theme.spacing.md)} ${pxToRem(18)};
  border: ${pxToRem(1)} solid ${({ theme }) => withAlpha(theme.colors.warningMuted, 0.5)};
  border-left-width: ${pxToRem(3)};
  border-radius: ${({ theme }) =>
    `${pxToRem(theme.radii.sm)} ${pxToRem(theme.radii.lg)} ${pxToRem(theme.radii.lg)} ${pxToRem(theme.radii.sm)}`};
  background: ${({ theme }) => withAlpha(theme.colors.warningMuted, 0.08)};

  ${({ $rated, theme }) =>
    $rated &&
    css`
      border-color: ${withAlpha(theme.colors.success, 0.5)};
      background: ${withAlpha(theme.colors.success, 0.07)};
    `}
`;

export const AssignmentReviewHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
  margin-bottom: ${({ theme }) => pxToRem(theme.spacing.xs)};
`;

export const AssignmentReviewFrom = styled.span<{ $rated: boolean }>`
  color: ${({ $rated, theme }) =>
    $rated ? theme.colors.successText : theme.colors.warningMutedText};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 500;
  line-height: 1.4;
`;

export const AssignmentReviewRating = styled.span`
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.4;

  strong {
    color: ${({ theme }) => theme.colors.ink};
    font-weight: 500;
  }
`;

export const AssignmentWaiting = styled.div`
  padding: ${({ theme }) => pxToRem(theme.spacing.lg)} ${pxToRem(18)};
  border: ${pxToRem(1)} dashed ${({ theme }) => theme.borders.outline};
  border-radius: ${({ theme }) => pxToRem(theme.radii.lg)};
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.5;
`;

export const AssignmentAnswerMeta = styled.div`
  margin-bottom: ${({ theme }) => pxToRem(theme.spacing.xs)};
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  line-height: 1.4;
`;

export const AssignmentForm = styled.form`
  margin: 0;
`;

export const AssignmentField = styled.div`
  margin-bottom: ${pxToRem(14)};

  &:last-of-type {
    margin-bottom: 0;
  }
`;

export const AssignmentLabel = styled.label`
  display: block;
  margin-bottom: ${pxToRem(6)};
  color: ${({ theme }) => theme.colors.inkSoft};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  line-height: 1.4;
`;

export const AssignmentTextareaField = styled.div<{ $hasError?: boolean }>`
  textarea {
    width: 100%;
    min-height: ${pxToRem(132)};
    padding: ${pxToRem(10)} ${({ theme }) => pxToRem(theme.spacing.sm)};
    border: ${pxToRem(1)} solid
      ${({ $hasError, theme }) =>
        $hasError ? theme.colors.dangerBright : withAlpha(theme.colors.ink, 0.26)};
    border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
    background: ${({ theme }) => theme.colors.white};
    color: ${({ theme }) => theme.colors.ink};
    font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
    font-weight: 400;
    line-height: 1.5;
    resize: vertical;
    overflow: auto;
    outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &::placeholder {
      color: ${({ theme }) => theme.colors.inkMuted};
    }

    &:hover:not(:disabled) {
      border-color: ${({ theme }) => withAlpha(theme.colors.ink, 0.38)};
    }

    &:focus-visible {
      border-color: ${({ theme }) => theme.colors.brand};
      box-shadow: inset 0 0 0 ${pxToRem(1)} ${({ theme }) => theme.colors.brand};
    }

    &:disabled {
      cursor: not-allowed;
      background: ${({ theme }) => theme.colors.surfaceFaint};
      color: ${({ theme }) => theme.colors.inkMuted};
    }
  }
`;

export const AssignmentUrlField = styled.div`
  .ios-input-container {
    width: 100%;
  }

  .ios-input-label {
    display: block;
    margin-bottom: ${pxToRem(6)};
    color: ${({ theme }) => theme.colors.inkSoft};
    font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
    line-height: 1.4;
  }

  .ios-input {
    width: 100%;
    height: ${pxToRem(42)};
    padding: 0 ${({ theme }) => pxToRem(theme.spacing.sm)};
    border: ${pxToRem(1)} solid ${({ theme }) => withAlpha(theme.colors.ink, 0.26)};
    border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
    background: ${({ theme }) => theme.colors.white};
    color: ${({ theme }) => theme.colors.ink};
    font-weight: 400;
    line-height: 1.5;
    outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  .ios-input:hover:not(:disabled) {
    border-color: ${({ theme }) => withAlpha(theme.colors.ink, 0.38)};
  }

  .ios-input:focus-visible {
    border-color: ${({ theme }) => theme.colors.brand};
    box-shadow: inset 0 0 0 ${pxToRem(1)} ${({ theme }) => theme.colors.brand};
  }

  .ios-input:disabled {
    cursor: not-allowed;
    background: ${({ theme }) => theme.colors.surfaceFaint};
    color: ${({ theme }) => theme.colors.inkMuted};
  }

  .ios-input-error {
    border-color: ${({ theme }) => theme.colors.dangerBright};
  }

  .ios-input-error-message {
    margin-top: ${pxToRem(6)};
    color: ${({ theme }) => theme.colors.dangerBright};
    font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  }
`;

export const AssignmentHint = styled.div`
  margin-top: ${pxToRem(6)};
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  line-height: 1.4;
`;

export const AssignmentError = styled.div`
  margin-top: ${pxToRem(6)};
  color: ${({ theme }) => theme.colors.dangerBright};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  line-height: 1.4;
`;

export const AssignmentFormError = styled(AssignmentError)`
  margin: 0 0 ${pxToRem(14)};
`;

export const AssignmentFormFoot = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => pxToRem(theme.spacing.sm)};
  flex-wrap: wrap;
  margin-top: ${({ theme }) => pxToRem(theme.spacing.md)};
`;

export const AssignmentSubmitButton = styled(Button)`
  width: auto;
  min-width: 0;
  max-width: none;
  height: ${({ theme }) => pxToRem(theme.controlHeight.sm)};
  padding: 0 ${pxToRem(18)};
  border-radius: ${({ theme }) => pxToRem(theme.radii.md)};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.sm)};
  font-weight: 500;
`;

export const AssignmentFormNote = styled.span`
  color: ${({ theme }) => theme.colors.inkMuted};
  font-size: ${({ theme }) => pxToRem(theme.fontSize.xs)};
  line-height: 1.4;
`;
